import React from 'react';

import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useConfigurationStore } from 'store/configurationsStore';

import { useBaseWidget } from 'hooks/useBaseWidget';

import ErrorBoundary from 'components/common/ErrorBoundary/ErrorBoundary';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import NoData from 'components/common/NoData/noData';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';

import BaselineVsActual from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/CommonComponents/BaselineVsActual/BaselineVsActual';
import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { AddToReport } from 'components/Dashboards/Reporting/AddToReport/AddToReport';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { Narrative } from 'components/Dashboards/Reporting/WidgetNarrativeWrapper/Narrative';
import { PinboardPills } from 'components/Dashboards/Reporting/pinboardPills/PinboardPills';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { LoaderContainer } from 'components/common';
import { useProgressSummaryLogic } from 'components/widgets/ProgressSummary/ProgressSummary.logic';
import { useProgressSummaryProvider } from 'components/widgets/ProgressSummary/ProgressSummary.provider';

export const ProgressSummary = ({
    projectId,
    contractId,
    latestVersionId,
    compareVersionId,
    globalFilterData,
    widgetId,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    localFilters,
    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title: propsTitle,
    setIsPayloadReady,
}: IComponentProps) => {
    const { componentKey, routeId: id, endpoints, name: configTitle } = useConfigurationStore().progressSummary;

    const { ui, api } = useProgressSummaryProvider({
        componentKey,
        widgetId,
        globalFilterData,
        localFilters,
        contractId,
        latestVersionId,
        compareVersionId,
        projectId,
        endpoints,
    });

    const { logic } = useProgressSummaryLogic({ ui, api, setIsPayloadReady, widgetViewMode });

    const { subTitle, route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title: propsTitle ?? configTitle,
        setSubTitleForExport,
        ui,
        reportWidgetRef,
        apiPayload: api.getBaselineVsActual.payload,
        widgetEndpoint: api.getBaselineVsActual.endpoint,
    });

    return (
        <ErrorBoundary>
                <WidgetWithTitle
                    id={id}
                    title={
                        <WidgetTitle
                            title={editableTitle}
                            setTitle={setEditableTitle}
                            widgetViewMode={widgetViewMode}
                        />
                    }
                    className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
                    titleComponents={[
                        externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                        <AddWidget
                            key={'AddWidget'}
                            componentKey={componentKey}
                            title={propsTitle ?? configTitle}
                            projectId={projectId}
                            contractId={contractId}
                            widgetId={widgetId}
                            route={route}
                            localFilters={ui.state}
                            shouldUseNewIcon={true}
                            shouldHide={true}
                        />,
                        <AddToReport
                            key={'addWidgetToReport'}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            projectId={projectId}
                            contractId={contractId}
                            uiState={ui.state}
                            shouldHide={WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)}
                            apiPayload={api.getBaselineVsActual.payload}
                            widgetEndpoint={api.getBaselineVsActual.endpoint}
                        />,
                        !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                            <ExportDropdown key={'export'} title={propsTitle ?? configTitle} subTitle={subTitle} />
                        ),
                        <TooltipIcon key={'info'} tooltip={api.getBaselineVsActual.instance.data?.info} />,
                        !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                            <ThreeDotsMenu
                                widgetViewMode={widgetViewMode}
                                widgetId={widgetId}
                                componentKey={componentKey}
                                shareTitle={propsTitle ?? configTitle}
                                shareLink={route}
                                projectId={projectId}
                                contractId={contractId}
                            />
                        ),
                    ]}
                    titleFilters={[
                        <ReportingVersionAndPills
                            key={'ReportingVersionAndPills'}
                            selectedProjectAndContract={widgetSelectedProjectAndContract}
                            pills={ui.pills}
                            widgetViewMode={widgetViewMode}
                        />,
                        <Narrative key={'narrative'} editNarrative={editNarrative} />,
                        <PinboardPills
                            key={'pinboardPills'}
                            isInPinboard={ui.isPinboard}
                            pills={ui.pills}
                            widgetViewMode={widgetViewMode}
                        />,
                    ]}
                >
                    {api.getBaselineVsActual.instance.data &&
                        !api.getBaselineVsActual.instance.isLoading &&
                        !logic.isNoData && (
                            <BaselineVsActual
                                data={api.getBaselineVsActual.instance.data}
                                loading={api.getBaselineVsActual.instance.isLoading}
                                progressWeighted={ui.state.progressMeasurement}
                                milestoneFragnetTaskVersionHashCode={logic.milestoneFragnetTaskVersionHashCode}
                                displayTable={!WidgetViewModeUtils.isInAnyReportMode(widgetViewMode)}
                            />
                        )}
                    {api.getBaselineVsActual.instance.isLoading &&
                        !api.getBaselineVsActual.instance.data &&
                        logic.isNoData && (
                            <LoaderContainer>
                                <OverlayWithSpinner />
                            </LoaderContainer>
                        )}
                    {!api.getBaselineVsActual.instance.isLoading && logic.isNoData && (
                        <NoData>{logic.notApplicable ? 'Not applicable with applied filter' : null}</NoData>
                    )}
                </WidgetWithTitle>
        </ErrorBoundary>
    );
};
